import * as XLSX from 'xlsx';

// The shipments spreadsheet: one row per shipment line, with the shipment's
// and its container's fields repeated on every row. Export writes it, import
// reads it back (any edited copy of an export works).

export const SHEET_COLUMNS = [
  'Shipment', 'Invoice no', 'Invoice date', 'Shipping line', 'Vessel', 'Port of loading', 'Port of discharge',
  'ETD', 'ETA', 'BL', 'Yard', 'State', 'Notes',
  'Container', 'Container state', 'Cleared',
  'PO', 'SKU', 'Product', 'Incoterm', 'Quantity', 'Unit', 'Currency', 'Unit price',
] as const;

type Row = Record<(typeof SHEET_COLUMNS)[number], string | number>;

const STATE_LABEL: Record<string, string> = {
  BOOKED: 'Booked', 'IN TRANSIT': 'In transit', ARRIVED: 'Arrived', CLEARED: 'Cleared',
  DELIVERED: 'Delivered', RECEIVED: 'Received', CANCELLED: 'Cancelled',
};

const day = (v: unknown): string => (v ? String(v).slice(0, 10) : '');

// ---- Export ----
export function writeShipmentsSheet(shipments: any[], fileName: string): number {
  const rows: Row[] = [];
  for (const s of shipments) {
    const base = {
      Shipment: s.reference, 'Invoice no': s.invoiceNumber ?? '', 'Invoice date': day(s.invoiceDate),
      'Shipping line': s.shippingLine ?? '', Vessel: s.vesselDetails ?? '',
      'Port of loading': s.portOfLoading ?? '', 'Port of discharge': s.portOfDischarge ?? '',
      ETD: day(s.etd), ETA: day(s.eta), BL: s.billOfLading ?? '', Yard: s.store,
      State: STATE_LABEL[s.status] ?? s.status, Notes: s.notes ?? '',
    };
    const empty = { Container: '', 'Container state': '', Cleared: '', PO: '', SKU: '', Product: '', Incoterm: '', Quantity: '', Unit: '', Currency: '', 'Unit price': '' };
    const containers: any[] = s.containers ?? [];
    if (!containers.length && !(s.items ?? []).length) rows.push({ ...base, ...empty });
    for (const c of containers.length ? containers : [null]) {
      const cFields = c
        ? { Container: c.containerNo ?? '', 'Container state': STATE_LABEL[c.status || s.status] ?? '', Cleared: day(c.clearedAt) }
        : { Container: '', 'Container state': '', Cleared: '' };
      const items = (s.items ?? []).filter((i: any) => (c ? i.containerId === c.id : true));
      if (!items.length) rows.push({ ...base, ...empty, ...cFields });
      for (const i of items) {
        rows.push({
          ...base, ...cFields,
          PO: i.poReference ?? '', SKU: i.sku ?? '', Product: i.productName, Incoterm: i.incoterm ?? '',
          Quantity: i.quantity, Unit: i.unitOfMeasure, Currency: i.currency, 'Unit price': i.unitPrice,
        });
      }
    }
  }

  const sheet = XLSX.utils.json_to_sheet(rows, { header: [...SHEET_COLUMNS] });
  sheet['!cols'] = SHEET_COLUMNS.map((c) => ({ wch: ['Product', 'Notes'].includes(c) ? 40 : ['Vessel', 'Shipping line', 'SKU'].includes(c) ? 24 : 14 }));
  const help = XLSX.utils.aoa_to_sheet([
    ['Importing shipments'],
    [''],
    ['One row per shipment line. The shipment and container columns repeat on every row of that shipment / container.'],
    ['Shipment: an existing reference (e.g. SH1006) updates that shipment. Any other label (e.g. NEW1, NEW2) creates a new shipment - rows with the same label go together. Blank = one new shipment.'],
    ['Container: rows with the same container number go in the same container. Container state / Cleared can be set per container.'],
    ['States: Booked, In transit, Arrived, Cleared, Delivered, Cancelled. "Received" is only set by checking a container in at Incoming Stocks.'],
    ['PO must be an existing purchase order reference. SKU is matched to the product; an unknown SKU is kept as a description.'],
    ['Dates: YYYY-MM-DD or DD/MM/YYYY.'],
    ['Updating a shipment replaces its lines and containers with what is in the file - containers left out are removed. Containers already received are never changed.'],
    ['You will see every change before anything is saved.'],
  ]);
  help['!cols'] = [{ wch: 140 }];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Shipments');
  XLSX.utils.book_append_sheet(workbook, help, 'How to import');
  XLSX.writeFile(workbook, fileName);
  return rows.length;
}

// ---- Import ----
// Any date cell (a real date, 2026-10-17 or 17/10/2026) as YYYY-MM-DD.
function toDate(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date && !isNaN(v.getTime())) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
  }
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  return s;
}

const text = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim());

// "In transit" / "in-transit" / "IN TRANSIT" -> IN TRANSIT
function toState(v: unknown): string | null {
  const s = text(v).toUpperCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  return s || null;
}

export interface ParsedSheet {
  shipments: any[];
  rows: number;
}

export async function readShipmentsSheet(file: File): Promise<ParsedSheet> {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
  const sheet = workbook.Sheets['Shipments'] ?? workbook.Sheets[workbook.SheetNames[0]];
  const raw = sheet ? XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' }) : [];
  // Headers matched loosely, so "invoice no." or "ETA " still count.
  const norm = (h: string) => h.toLowerCase().replace(/[^a-z]/g, '');
  const column = new Map(SHEET_COLUMNS.map((c) => [norm(c), c]));
  const rows = raw.map((r) => {
    const out: Partial<Record<(typeof SHEET_COLUMNS)[number], unknown>> = {};
    for (const [k, v] of Object.entries(r)) {
      const c = column.get(norm(k));
      if (c) out[c] = v;
    }
    return out;
  });

  const groups = new Map<string, { rows: { r: (typeof rows)[number]; row: number }[] }>();
  rows.forEach((r, i) => {
    if (Object.values(r).every((v) => text(v) === '')) return;
    const key = text(r.Shipment) || 'NEW';
    if (!groups.has(key)) groups.set(key, { rows: [] });
    groups.get(key)!.rows.push({ r, row: i + 2 }); // +2: header row, 1-based
  });

  const first = (list: { r: any }[], col: (typeof SHEET_COLUMNS)[number], fn: (v: unknown) => any = text) => {
    for (const { r } of list) if (text(r[col]) !== '') return fn(r[col]);
    return null;
  };

  const shipments = [...groups].map(([key, g]) => {
    const containerNos: string[] = [];
    g.rows.forEach(({ r }) => {
      const no = text(r.Container).toUpperCase();
      if (!containerNos.includes(no)) containerNos.push(no);
    });
    return {
      key,
      reference: /^SH\d+$/i.test(key) ? key.toUpperCase() : null,
      invoiceNumber: first(g.rows, 'Invoice no'),
      invoiceDate: first(g.rows, 'Invoice date', toDate),
      shippingLine: first(g.rows, 'Shipping line'),
      vesselDetails: first(g.rows, 'Vessel'),
      portOfLoading: first(g.rows, 'Port of loading', (v) => text(v).toUpperCase()),
      portOfDischarge: first(g.rows, 'Port of discharge', (v) => text(v).toUpperCase()),
      etd: first(g.rows, 'ETD', toDate),
      eta: first(g.rows, 'ETA', toDate),
      billOfLading: first(g.rows, 'BL'),
      store: first(g.rows, 'Yard', (v) => {
        const y = text(v).toLowerCase();
        return y.startsWith('k') ? 'Kettering' : y.startsWith('m') ? 'Markstey' : text(v);
      }),
      status: first(g.rows, 'State', toState),
      notes: first(g.rows, 'Notes'),
      containers: containerNos.map((no) => {
        const inIt = g.rows.filter(({ r }) => text(r.Container).toUpperCase() === no);
        return { containerNo: no || null, status: first(inIt, 'Container state', toState), clearedAt: first(inIt, 'Cleared', toDate) };
      }),
      lines: g.rows
        .filter(({ r }) => text(r.SKU) || text(r.Product) || text(r.PO) || text(r.Quantity))
        .map(({ r, row }) => ({
          row,
          containerNo: text(r.Container).toUpperCase() || null,
          poReference: text(r.PO) || null,
          sku: text(r.SKU) || null,
          productName: text(r.Product) || null,
          incoterm: text(r.Incoterm) || null,
          quantity: Number(r.Quantity) || 0,
          unitOfMeasure: text(r.Unit) || null,
          currency: text(r.Currency) || null,
          unitPrice: Number(r['Unit price']) || 0,
        })),
    };
  });
  return { shipments, rows: rows.length };
}
